import React, { useRef, useEffect, useState, useCallback } from "react";
import { BiSend, BiSmile } from "react-icons/bi";
import { SlClose, SlPaperClip } from "react-icons/sl";
import styles from "../../../style/components/chat/ChatInput.module.css";
import EmojiPicker from "emoji-picker-react";
import { getFileTypeIcon } from "../../../utils/fileHelpers";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { GoMention } from "react-icons/go";
import ChatMension from "../ChatMension";
import { Emoji } from "emoji-picker-react";

const ChatInput = ({
  text,
  setText,
  uploadedFiles,
  handleChange,
  handleFileChange,
  handleRemoveFile,
  handleSend,
  inputRef,
  onHeightChange,
  insertMentionUser,
  setfocusedMessageId = () => "",
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState([]);

  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);
  const [showMension, setShowMension] = useState(false);
  const modules = {
    toolbar: [["bold", "italic", "underline"]],
    clipboard: {
      matchVisual: false,
    },
  };
  const focusEditor = () => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor.focus();
    }
  };

  useEffect(() => {
    focusEditor();
  }, []);

  const adjustHeight = useCallback(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const maxHeight = 300;
    const editorHeight = Math.min(editor.container.scrollHeight, maxHeight);
    if (onHeightChange) {
      const extraHeightForFiles = uploadedFiles.length > 0 ? 90 : 0;
      const totalHeight = Math.min(
        editorHeight + extraHeightForFiles,
        maxHeight
      );
      onHeightChange(totalHeight);
    }
  }, [onHeightChange, uploadedFiles]);

  useEffect(() => {
    insertMentionUser(mentionedUsers);
  }, [mentionedUsers, insertMentionUser]);

  const handleEditorChange = useCallback(
    (content, delta, source, editor) => {
      if (handleChange) {
        handleChange(editor.getContents().ops);
      }
      adjustHeight();

      const plainText = editor.getText();
      const mentionRegex = /@([^\s]+)/g;
      let match;
      const mentionedNames = [];

      while ((match = mentionRegex.exec(plainText)) !== null) {
        mentionedNames.push(match[1]);
      }

      setMentionedUsers((prev) => {
        const updated = prev.filter((u) =>
          mentionedNames.includes(u.first_name)
        );
        return updated;
      });
    },
    [handleChange, adjustHeight]
  );

  useEffect(() => {
    adjustHeight();
  }, [text, uploadedFiles, adjustHeight]);

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file && file.type.startsWith("image/")) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        e.preventDefault(); // 🚫 STOP Quill from inserting image
        handleFileChange({
          target: { files },
        });
      }
    },
    [handleFileChange]
  );

  useEffect(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();

    const editorContainer = editor.root;
    editorContainer.addEventListener("paste", handlePaste);

    return () => {
      editorContainer.removeEventListener("paste", handlePaste);
    };
  }, [handlePaste]);

  useEffect(() => {
    if (!inputRef.current) return;

    const maxTextHeight = 150;
    const extraHeightForFiles = uploadedFiles.length > 0 ? 90 : 0;
    const maxTotalHeight = 300;

    inputRef.current.style.height = "auto";
    const textHeight = Math.min(inputRef.current.scrollHeight, maxTextHeight);
    const totalHeight = Math.min(
      textHeight + extraHeightForFiles,
      maxTotalHeight
    );

    inputRef.current.style.height = `${textHeight}px`;

    if (onHeightChange) {
      onHeightChange(totalHeight);
    }
  }, [uploadedFiles, onHeightChange, inputRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEmojiClick = (emojiData) => {
    if (!quillRef.current) return;

    const editor = quillRef.current.getEditor();
    editor.focus();

    setTimeout(() => {
      const range = editor.getSelection() || {
        index: editor.getLength(),
        length: 0,
      };

      const emojiImg = document.createElement("span");
      emojiImg.style.display = "inline-block";
      emojiImg.style.verticalAlign = "middle";

      const temp = document.createElement("div");
      document.body.appendChild(temp);

      import("react-dom").then((ReactDOM) => {
        ReactDOM.render(
          <Emoji unified={emojiData.unified} size={8} />,
          emojiImg
        );

        setTimeout(() => {
          const emojiHTML = emojiImg.innerHTML;

          editor.clipboard.dangerouslyPasteHTML(range.index, emojiHTML);
          editor.setSelection(range.index + 1);
          focusEditor();

          setShowEmojiPicker(false);

          document.body.removeChild(temp);
        }, 10);
      });
    }, 0);
  };

  const getFilePreview = (file) => {
    if (file.type.startsWith("image/")) {
      return (
        <img
          src={file.url}
          alt={file.name}
          className={styles.filePreviewImage}
          onLoad={() => {
            if (file.url && !file.url.startsWith("blob:")) {
              URL.revokeObjectURL(file.url);
            }
          }}
        />
      );
    }
    return (
      <span className={styles.FileIcon}>
        <img src={getFileTypeIcon(file.name)} alt={file.name} />
      </span>
    );
  };

  const insertMention = (user) => {
    const editor = quillRef.current.getEditor();
    editor.focus();
    focusEditor();

    setTimeout(() => {
      const range = editor.getSelection();
      const mentionText = `@${user.first_name}`;

      if (range) {
        editor.insertText(range.index, mentionText, "user");
        editor.insertText(range.index + mentionText.length, " ");
        editor.setSelection(range.index + mentionText.length + 1);
      } else {
        const endIndex = editor.getLength();
        editor.insertText(endIndex, mentionText + " ");
        editor.setSelection(endIndex + mentionText.length + 1);
      }
    }, 0);
    setMentionedUsers((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      const updated = exists ? prev : [...prev, user];
      insertMentionUser(updated); // send the array
      return updated;
    });
  };

  const sendMessage = () => {
    if (quillRef.current) {
      const delta = quillRef.current.getEditor().getContents();
      const hasContent =
        delta.ops &&
        delta.ops.some((op) => {
          if (typeof op.insert === "string") {
            return op.insert.trim().length > 0;
          }
          return typeof op.insert === "object";
        });

      const hasFiles = uploadedFiles.length > 0;

      if (hasContent || hasFiles) {
        handleSend({ text: delta.ops, files: uploadedFiles });
        quillRef.current.getEditor().setContents([]);
        setText([]);
        setfocusedMessageId("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        focusEditor();
      }
    }
  };

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.focus();
      }
    }
  }, [uploadedFiles]);

  return (
    <div className={styles.ChatInputMaincontentBottom}>
      <div className={styles.ChatInputMaincontentBottomsb}>
        <div className={styles.ChatInputInputBottom}>
          <div className={styles.inputContainer}>
            <div className={styles.inputDiv}>
              <ReactQuill
                ref={quillRef}
                value={{ ops: text }}
                onChange={handleEditorChange}
                modules={modules}
                placeholder="Type a message..."
                className={styles.contentTextDiv}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <div className={styles.ChatInputButtomIcons}>
                <label htmlFor="fileInput" className={styles.fileInputLabel}>
                  <SlPaperClip />
                </label>
                <BiSmile onClick={() => setShowEmojiPicker(!showEmojiPicker)} />
                <GoMention onClick={() => setShowMension(true)} />

                {showEmojiPicker && (
                  <div className={styles.emojiPickerMain}>
                    <div
                      className={styles.emojiPickerWrapper}
                      ref={emojiPickerRef}
                      onMouseLeave={() => setShowEmojiPicker(false)}
                    >
                      <EmojiPicker
                        onEmojiClick={handleEmojiClick}
                        previewConfig={{ showPreview: false }}
                        skinTonesDisabled
                        searchDisabled
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.sendIcon} onClick={sendMessage}>
              <BiSend size={25} />
            </div>

            <input
              type="file"
              onChange={handleFileChange}
              id="fileInput"
              ref={fileInputRef}
              multiple
              className={styles.ChatfileUploadInput}
            />
          </div>

          {uploadedFiles.length > 0 && (
            <div className={styles.uploadedFilesContainer}>
              <div className={styles.uploadedFilesScroller}>
                {uploadedFiles.map((item) => (
                  <div key={item.id} className={styles.uploadedFileItem}>
                    {getFilePreview(item)}
                    <span className={styles.FileName}>{item.name}</span>
                    <button
                      className={styles.removeFileButton}
                      onClick={() => handleRemoveFile(item.id)}
                    >
                      <SlClose />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {showMension && (
        <ChatMension
          Onclose={() => setShowMension(false)}
          onSelectUser={insertMention}
        />
      )}
    </div>
  );
};

export default ChatInput;
